import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "../hooks/useTranslation";
import { api } from "../api/client";
import { GRIGIO, SCURO } from "../components/integrations/casella";
import { CAMPO_TESTO, Campo, RigaInterruttore } from "../components/widget/campi";
import { trackEvent } from "../lib/analytics";
import { apriPiani } from "../utils/limitiPiano";
import {
  Loader2,
  Search,
  CheckCircle2,
  XCircle,
  Package,
  AlertTriangle,
} from "lucide-react";

// Labels resolved via i18n (productPreview.fields.* / productPreview.demoElements.*)
const FIELD_KEYS = [
  "title",
  "image",
  "description",
  "price",
  "currency",
  "availability",
];

// Demo toggles: each one tells the live widget (right column) to render that
// chat element, 1:1 with the real widget. Keys match the flags handled by the
// widget's pingup-preview-demo handler in ChatApp.jsx.
const DEMO_ELEMENT_KEYS = [
  "text",
  "sources",
  "products",
  "typing",
  "quickReplies",
  "whatsapp",
];

// Real widget product-card CSS (ported from the widget's base-css.js, scoped
// under .rag-card-preview) so the preview matches the bot 1:1. Theme colors are
// supplied via CSS variables on the wrapper.
const RAG_CARD_CSS = `
.rag-card-preview .rag-mini-card { flex-shrink:0; width:200px; background:var(--bg,#fff); border:1px solid #f1f3f5; border-radius:12px; overflow:hidden; box-shadow:0 1px 3px rgba(0,0,0,.04); display:flex; flex-direction:column; text-decoration:none; color:inherit; transition:all .3s cubic-bezier(0.4,0,0.2,1); }
.rag-card-preview .rag-mini-card-link { cursor:pointer; }
.rag-card-preview .rag-mini-card-link:hover { box-shadow:0 8px 30px rgba(0,0,0,.08); transform:translateY(-2px); border-color:#e8eaed; }
.rag-card-preview .rag-mini-card-image { width:100%; aspect-ratio:4/3; object-fit:cover; background:#fafbfc; }
.rag-card-preview .rag-mini-card-placeholder { width:100%; aspect-ratio:4/3; display:flex; align-items:center; justify-content:center; background:#fafbfc; font-size:24px; color:#d1d5db; }
.rag-card-preview .rag-mini-card-content { padding:16px; flex:1; display:flex; flex-direction:column; gap:6px; }
.rag-card-preview .rag-mini-card-title { font-size:calc(var(--font-size,14px) - 1px); font-weight:600; margin:0; color:#18181b; line-height:1.4; overflow:hidden; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; }
.rag-card-preview .rag-mini-card-subtitle { font-size:calc(var(--font-size,14px) - 2px); margin:0; color:#71717a; line-height:1.5; }
.rag-card-preview .rag-mini-card-footer { padding:16px; padding-top:0; }
.rag-card-preview .rag-mini-card-cta { width:100%; padding:10px 16px; border:none; border-radius:8px; background:linear-gradient(135deg, var(--primary,#2563eb) 0%, color-mix(in srgb, var(--primary,#2563eb) 85%, #000) 100%); color:#fff; font-weight:600; font-size:calc(var(--font-size,14px) - 2px); text-align:center; display:block; box-sizing:border-box; letter-spacing:0.2px; }
`;

function formatPrice(price, currency) {
  if (price == null) return null;
  if (!currency) return String(price);
  try {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency,
    }).format(price);
  } catch {
    return `${price} ${currency}`;
  }
}

export default function ProductPreviewPage({
  bot,
  onProductLoaded,
  demo = {},
  onDemoChange,
}) {
  const { t } = useTranslation();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [needsUpgrade, setNeedsUpgrade] = useState(false);
  const upsellingTracked = useRef(false);

  const handlePreview = async (e) => {
    e?.preventDefault?.();
    if (!url.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.products.preview(url.trim());
      setResult(res);
    } catch (err) {
      // Plan gating: the API returns 402 feature_not_available on plans
      // without product_suggestions (e.g. LITE) → show the upgrade prompt
      if (err.message === "feature_not_available") {
        if (!upsellingTracked.current) {
          upsellingTracked.current = true;
          trackEvent("bot_ai_shop_upselling");
        }
        setNeedsUpgrade(true);
        // Come per ogni limite del piano: chi puo' comprare vede subito i piani.
        apriPiani({ feature: "product_suggestions" });
      } else {
        setError(err.message || t("productPreview.previewError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const product = result?.product;

  // Mirror the loaded product into the live chat preview (right column).
  useEffect(() => {
    onProductLoaded?.(product || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const sp = product?.schema_product || {};
  const compatibility = result?.compatibility || {};
  const hasMissing = Object.values(compatibility).some((v) => !v);

  // Bot theme → CSS variables so the real card matches the bot's colors.
  const theme = bot?.widget_config?.theme || {};
  const cardVars = {
    "--primary": theme.primaryColor || "#2563eb",
    "--bg": theme.backgroundColor || "#fff",
    "--font-size": `${theme.fontSize || 14}px`,
  };
  const subtitle = [sp.brand, formatPrice(sp.price, sp.currency)]
    .filter(Boolean)
    .join(", ");

  const toggleDemo = (key) =>
    onDemoChange?.({ ...demo, [key]: !demo?.[key] });
  const anyDemoOn = DEMO_ELEMENT_KEYS.some((key) => demo?.[key]);

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <div>
          <h2 className="text-sm font-semibold text-[#020617]">
            {t("productPreview.title")}
          </h2>
          <p className="mt-0.5 text-xs leading-5 text-[#64748B]">
            {t("productPreview.subtitle")}
          </p>
        </div>

        <form onSubmit={handlePreview}>
          <Campo
            id="prova-prodotto-url"
            etichetta={t("productPreview.urlLabel", "Indirizzo della pagina prodotto")}
          >
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                id="prova-prodotto-url"
                type="url"
                placeholder={t("productPreview.urlPlaceholder")}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={loading}
                className={`${CAMPO_TESTO} min-w-0 sm:flex-1 disabled:bg-[#F8FAFC] disabled:text-[#64748B]`}
              />
              <button
                type="submit"
                disabled={loading || !url.trim()}
                className={`${SCURO} h-10 shrink-0`}
              >
                {loading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Search className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {t("productPreview.previewButton")}
              </button>
            </div>
          </Campo>
        </form>

        {error && (
          <div className="flex items-start gap-2 rounded-[10px] border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {error}
          </div>
        )}

        {needsUpgrade && (
          <div className="rounded-[10px] border border-[#E2E8F0] bg-[#F8FAFC] p-4">
            <p className="text-sm font-semibold text-[#020617]">
              {t("productPreview.upgradeTitle")}
            </p>
            <p className="mt-1 text-xs leading-5 text-[#64748B]">
              {t("productPreview.upgradeBody")}
            </p>
            <a href="/billing" className={`${SCURO} mt-3`}>
              {t("productPreview.upgradeCta")}
            </a>
          </div>
        )}

        {result && !result.found && (
          <div className="flex items-start gap-2.5 rounded-[10px] border border-[#E2E8F0] bg-[#F8FAFC] p-4">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-[#020617]">
                {t("productPreview.notDetectedTitle")}
              </p>
              <p className="mt-1 text-xs leading-5 text-[#64748B]">{result.message}</p>
            </div>
          </div>
        )}

        {result?.found && product && (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1 2xl:grid-cols-2">
            <div className="space-y-1.5">
              <p className="flex items-center gap-1.5 text-xs font-medium text-[#334155]">
                <Package className="h-3.5 w-3.5 text-[#64748B]" aria-hidden="true" />
                {t("productPreview.botCardTitle")}
              </p>
              <style>{RAG_CARD_CSS}</style>
              <div
                className="rag-card-preview flex justify-center rounded-[10px] border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-5"
                style={cardVars}
              >
                <a
                  className="rag-mini-card rag-mini-card-link"
                  href={product.canonical_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {product.image_url ? (
                    <img
                      className="rag-mini-card-image"
                      src={product.image_url}
                      alt={product.title || t("productPreview.productAlt")}
                    />
                  ) : (
                    <div className="rag-mini-card-placeholder">
                      <Package className="h-6 w-6" aria-hidden="true" />
                    </div>
                  )}
                  <div className="rag-mini-card-content">
                    <h3 className="rag-mini-card-title">
                      {product.title || "—"}
                    </h3>
                    {subtitle && (
                      <p className="rag-mini-card-subtitle">{subtitle}</p>
                    )}
                  </div>
                  <div className="rag-mini-card-footer">
                    <span className="rag-mini-card-cta">
                      {t("productPreview.learnMore")}
                    </span>
                  </div>
                </a>
              </div>
            </div>

            <div className="space-y-1.5">
              <div>
                <p className="text-xs font-medium text-[#334155]">
                  {t("productPreview.compatibilityTitle")}
                </p>
                <p className="text-xs leading-5 text-[#64748B]">
                  {t("productPreview.compatibilityDesc")}
                </p>
              </div>
              <ul className="divide-y divide-[#E2E8F0] rounded-[10px] border border-[#E2E8F0]">
                {FIELD_KEYS.map((key) => (
                  <li key={key} className="flex items-center gap-2 px-3 py-2 text-sm">
                    {compatibility[key] ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-[#0F7A3D]" />
                    ) : (
                      <XCircle className="h-4 w-4 shrink-0 text-red-500" />
                    )}
                    <span className={compatibility[key] ? "text-[#020617]" : "text-[#64748B]"}>
                      {t(`productPreview.fields.${key}`)}
                    </span>
                  </li>
                ))}
              </ul>
              {hasMissing && (
                <p className="pt-1 text-xs leading-5 text-[#64748B]">
                  {t("productPreview.missingFieldsNote")}
                </p>
              )}
            </div>
          </div>
        )}
      </section>

      <section className="space-y-4 border-t border-[#E2E8F0] pt-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-[#020617]">
              {t("productPreview.demoTitle")}
            </h2>
            <p className="mt-0.5 text-xs leading-5 text-[#64748B]">
              {t("productPreview.demoDesc")}
            </p>
          </div>
          {anyDemoOn && (
            <button
              type="button"
              className={`${GRIGIO} shrink-0`}
              onClick={() => onDemoChange?.({})}
            >
              {t("productPreview.resetAll")}
            </button>
          )}
        </div>
        <div className="space-y-4">
          {DEMO_ELEMENT_KEYS.map((key) => (
            <RigaInterruttore
              key={key}
              id={`prova-demo-${key}`}
              etichetta={t(`productPreview.demoElements.${key}`)}
              attivo={demo?.[key]}
              onCambia={() => toggleDemo(key)}
            />
          ))}
        </div>
        <p className="text-xs leading-5 text-[#94A3B8]">
          {t("productPreview.runtimeNote")}
        </p>
      </section>
    </div>
  );
}
